import calculateRoadTax, { type CalcRoadTaxResult } from "@/utils/rto";
import { getInsuranceEstimate, type InsuranceEstimate } from "@/utils/insurance";
import { findSpecValue } from "@/app/utilities/modelTagConverter";
import {
    calculateEMI,
    getDefaultDownPayment,
    EMI_DEFAULT_INTEREST_RATE,
    EMI_DEFAULT_TENURE,
} from "@/lib/utils/emi";
import type { CityData } from "@/types/city";

export type { CityData } from "@/types/city";

export interface OnRoadPriceResult {
    basePrice: number;
    rtoEstimate: CalcRoadTaxResult | null;
    insuranceEstimate: InsuranceEstimate | null;
    onRoadPrice: number;
    defaultEmi: number;
}

/**
 * Centralized on-road price calculator. Accepts any variant shape (server or client)
 * and a cityData object (from getCityBySlug or the location cookie).
 *
 * Returns null when there is no base price or the required inputs are missing.
 *
 * @param variant  - Any variant object that findSpecValue can read
 * @param cityData - City/location object with a `state` property containing RTO config
 */
export function getOnRoadPrice(variant: any, cityData: CityData | null | undefined): OnRoadPriceResult | null {
    if (!variant || !cityData) return null;

    const basePrice = Number(variant.price) || 0;
    if (!basePrice) return null;

    const stateCfg = cityData.state || {};
    const variantFuel = variant.fuel_types?.[0] != null ? variant.fuel_types?.[0] : '';
    const isEV = variantFuel.toLowerCase() === "electric" || variant.fuel_types?.some((t: string) => t?.toLowerCase() === "electric") === true;
    const engineCcNum = findSpecValue(variant, "engine_displacement") != '' ? parseInt(findSpecValue(variant, "engine_displacement")) : 0;
    const motorPower = findSpecValue(variant, "motor_power") != '' ? parseInt(findSpecValue(variant, "motor_power")) : 0;
    
    // For RTO calculation, we need to pass both Engine CC and Price for accurate slab matching in states like Karnataka and Delhi. We will NOT fallback CNG→PETROL for RTO calculation as it can lead to incorrect slab matches and thus incorrect tax estimates.
    const rtoEstimate = calculateRoadTax({
        base: basePrice,
        engineCc: engineCcNum || undefined,
        fuelType: String(variantFuel),
        ownershipType: "Individual",
        stateConfig: {
            stateCode: stateCfg?.slug ?? '',
            rtoSlabs: stateCfg.rtoSlabs || [],
            secondVehicleTaxPercent: stateCfg.secondVehicleTaxPercent,
        },
        isSecondVehicle: false,
    });

    // For insurance, we need to determine the correct "power" metric: Engine CC for ICE vehicles and Motor kW for EVs.
    const powerForInsurance = isEV ? motorPower : engineCcNum;
    const insuranceEstimate = getInsuranceEstimate(
        basePrice,
        isEV,
        Number(powerForInsurance),
        cityData?.cityTier === 'Tier 1' ? 'A' : 'B',
    );
    const onRoadPrice = basePrice + (rtoEstimate?.total || 0) + (insuranceEstimate?.total || 0);
    const defaultEmi = calculateEMI({
        price: onRoadPrice,
        downPayment: getDefaultDownPayment(onRoadPrice),
        interestRate: EMI_DEFAULT_INTEREST_RATE,
        tenureMonths: EMI_DEFAULT_TENURE,
    }).emi;

    return { basePrice, rtoEstimate, insuranceEstimate, onRoadPrice, defaultEmi };
}
